import React, { useEffect, useState } from 'react';
import { Save, Star, Plus, Trash2, KeyRound } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Field, Text, Area, StringList, ObjectList } from './fields.jsx';

/** Texty a nastavení jednoho ročníku + založení dalšího a změna hesla. */
export default function YearAdmin({ year, years, notify, reload, username }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  // při přepnutí ročníku v hlavičce načteme jeho hodnoty do formuláře
  useEffect(() => {
    if (!year) return setForm(null);
    setForm({
      label: year.label,
      school: year.school,
      teacher: year.teacher,
      syllabus: year.syllabus || [],
      about: year.about || [],
      gradingNote: year.grading?.note || '',
      gradingBlocks: year.grading?.blocks || [],
      assignmentRules: year.assignmentRules || [],
    });
  }, [year]);

  if (!year || !form) return null;
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.updateYear(year.id, {
        label: form.label,
        school: form.school,
        teacher: form.teacher,
        // prázdné řádky nemá smysl ukládat
        syllabus: form.syllabus.filter((s) => s.trim()),
        about: form.about.filter((s) => s.trim()),
        grading: {
          note: form.gradingNote,
          blocks: form.gradingBlocks.filter((b) => b.body?.trim() || b.heading?.trim()),
        },
        assignmentRules: form.assignmentRules.filter((s) => s.trim()),
      });
      await reload();
      notify('Změny uloženy.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const makeCurrent = async () => {
    try {
      await api.admin.setCurrentYear(year.id);
      await reload();
      notify(`Ročník ${year.label} je nyní aktuální.`);
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <>
      <div className="admin-title">
        <div>
          <h1>Ročník a texty</h1>
          <p>{year.label}{year.isCurrent ? ' · aktuální ročník' : ' · archiv'}</p>
        </div>
        {!year.isCurrent && (
          <button type="button" className="btn" onClick={makeCurrent}>
            <Star size={16} /> Nastavit jako aktuální
          </button>
        )}
      </div>

      <form onSubmit={save}>
        <div className="panel">
          <h2>Základní údaje</h2>
          <p>Zobrazují se v hlavičce a na úvodní stránce.</p>
          <div className="field-row">
            <Field label="Označení ročníku" id="label"><Text id="label" value={form.label} onChange={(v) => set({ label: v })} /></Field>
            <Field label="Škola" id="skola"><Text id="skola" value={form.school} onChange={(v) => set({ school: v })} /></Field>
            <Field label="Vyučující" id="ucitel"><Text id="ucitel" value={form.teacher} onChange={(v) => set({ teacher: v })} /></Field>
          </div>
        </div>

        <div className="panel">
          <h2>Sylabus</h2>
          <p>Body, které se na úvodní stránce zobrazí jako číslovaný seznam.</p>
          <StringList value={form.syllabus} onChange={(syllabus) => set({ syllabus })} placeholder="Téma semináře" addLabel="Přidat bod" />
        </div>

        <div className="panel">
          <h2>O předmětu</h2>
          <p>Odstavce textu pod sylabem.</p>
          <StringList value={form.about} onChange={(about) => set({ about })} placeholder="Odstavec textu" multiline addLabel="Přidat odstavec" />
        </div>

        <div className="panel">
          <h2>Známkování</h2>
          <p>Poznámka se zobrazí v rámečku nahoře, části pod ní jako karty.</p>
          <Field label="Poznámka nahoře" id="grading-note">
            <Area id="grading-note" value={form.gradingNote} onChange={(v) => set({ gradingNote: v })} rows={2} />
          </Field>
          <Field label="Části pravidel">
            <ObjectList
              value={form.gradingBlocks}
              onChange={(gradingBlocks) => set({ gradingBlocks })}
              empty={{ heading: '', body: '' }}
              addLabel="Přidat část"
              fields={[
                { key: 'heading', label: 'Nadpis (např. 1. pololetí)' },
                { key: 'body', label: 'Text', type: 'textarea' },
              ]}
            />
          </Field>
        </div>

        <div className="panel">
          <h2>Pravidla úkolů</h2>
          <p>Odrážky nad seznamem úkolů.</p>
          <StringList value={form.assignmentRules} onChange={(assignmentRules) => set({ assignmentRules })} placeholder="Pravidlo" multiline addLabel="Přidat pravidlo" />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            <Save size={16} /> {busy ? 'Ukládám…' : 'Uložit změny'}
          </button>
        </div>
      </form>

      <NewYear years={years} notify={notify} reload={reload} />
      <PasswordPanel notify={notify} username={username} />
      <DangerZone year={year} years={years} notify={notify} reload={reload} />
    </>
  );
}

function NewYear({ notify, reload }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  // "2027/2028" -> "2027-2028"
  const slug = label.trim().replace(/\//g, '-').replace(/\s+/g, '');

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.createYear({
        slug,
        label: label.trim(),
        school: 'Gymnázium Nad Kavalírkou',
        teacher: 'Štěpán Kovář',
      });
      await reload();
      setLabel('');
      setOpen(false);
      notify('Ročník založen. Vyberte ho nahoře a doplňte texty.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginTop: '2.5rem' }}>
      <h2>Nový školní rok</h2>
      <p>Založí prázdný ročník. Stávající se tím nezmění — přepnete ho na aktuální, až budete chtít.</p>

      {open ? (
        <form onSubmit={create}>
          <Field label="Označení" id="novy-rocnik" hint={slug ? `Adresa archivu: /archiv/${slug}` : 'Např. 2027/2028'}>
            <Text id="novy-rocnik" value={label} onChange={setLabel} placeholder="2027/2028" autoFocus />
          </Field>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !slug}>Založit</button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Zrušit</button>
          </div>
        </form>
      ) : (
        <button type="button" className="btn" onClick={() => setOpen(true)}><Plus size={16} /> Založit ročník</button>
      )}
    </div>
  );
}

function PasswordPanel({ notify, username }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.auth.changePassword(current, next);
      setCurrent(''); setNext(''); setOpen(false);
      notify('Heslo změněno. Ostatní zařízení byla odhlášena.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <h2>Heslo</h2>
      <p>Přihlášen jako <strong>{username}</strong>.</p>

      {open ? (
        <form onSubmit={submit}>
          <Field label="Současné heslo" id="heslo-stare">
            <input id="heslo-stare" className="input" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
          </Field>
          <Field label="Nové heslo" id="heslo-nove" hint="Alespoň 10 znaků.">
            <input id="heslo-nove" className="input" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={10} required />
          </Field>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>Změnit heslo</button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Zrušit</button>
          </div>
        </form>
      ) : (
        <button type="button" className="btn" onClick={() => setOpen(true)}><KeyRound size={16} /> Změnit heslo</button>
      )}
    </div>
  );
}

function DangerZone({ year, years, notify, reload }) {
  // aktuální ani poslední zbylý ročník smazat nejde – hlídá to i server
  if (year.isCurrent || years.length <= 1) return null;

  const remove = async () => {
    const answer = window.prompt(
      `Smazání ročníku ${year.label} nenávratně odstraní ${year.lectures.length} hodin a ${year.assignments.length} úkolů.\n\nPro potvrzení opište označení ročníku:`,
    );
    if (answer?.trim() !== year.label) return;

    try {
      await api.admin.deleteYear(year.id);
      await reload();
      notify('Ročník smazán.');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <div className="panel">
      <h2>Smazání ročníku</h2>
      <p>Odstraní ročník {year.label} i s hodinami a úkoly. Nahrané soubory zůstanou.</p>
      <button type="button" className="btn btn-danger" onClick={remove}><Trash2 size={16} /> Smazat ročník</button>
    </div>
  );
}
